/* eslint-disable @typescript-eslint/ban-types */
import React, { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { InputAdornment, OutlinedInput, Typography, IconButton } from '@mui/material';
import {
  DragIndicatorOutlined as DragIndicatorOutlinedIcon,
  CloseOutlined as CloseOutlinedIcon,
  CropOriginalOutlined as CropOriginalOutlinedIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import ReactHtmlParser from 'react-html-parser';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { PALETTE, BUTTON } from '~/assets/styles/base';
import { ORIGINAL_FORM_VALIDATION } from '~/common/constants';
import { getOriginalFormsValidationMsg } from '~/common/functional';
import { MultipleAnswerConfigAnswerProps, MultipleAnswerConfigProps } from '~/common/types';
import InputValidationErrorTooltip from '~/components/ui-parts/InputValidationErrorTooltip';
import ImageUploadModal from '../../ImageUploadModal';
import { LOCALE_STRING } from '~/constants';

const ContentBox = styled.div``;

const AnswerComponent = styled.div`
  width: 716px;
  display: flex;
  flex-direction: row;
  padding: 16px 7px;
  height: auto;
  align-items: center;
`;

const AnswerInput = styled.div`
  width: 100%;
`;

const DragIconWrapper = styled(DragIndicatorOutlinedIcon)`
  color: ${BUTTON.dragIcon.unactive};
  fontsize: 16px;
  &:hover {
    color: ${BUTTON.dragIcon.active};
    cursor: move;
  }
  &:focus {
    outline: none;
  }
`;

const ImgPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StyledImg = styled.img`
  max-height: 170px;
`;

const StyledDisabledText = styled(Typography)`
  font-size: 14px;
  color: #425f77;
  width: 100%;
  margin-left: 15px;
`;

type Props = {
  id: string;
  questionIdx: number;
  totalQuestions: number;
  answerIdx: number;
  handleAnswerChange: Function;
  isValidate?: boolean;
  item: MultipleAnswerConfigAnswerProps;
  answersListComponents: MultipleAnswerConfigAnswerProps[];
  setAnswersListComponents: Function;
  handleFormValidationError: Function;
  multipleAnswerConfig: MultipleAnswerConfigProps;
};

const SortableItem = ({
  id,
  questionIdx,
  totalQuestions,
  answerIdx,
  handleAnswerChange,
  isValidate,
  item,
  answersListComponents,
  setAnswersListComponents,
  handleFormValidationError,
  multipleAnswerConfig,
}: Props) => {
  const { t } = useTranslation();

  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [imageUploadAnswerIdx, setImageUploadAnswerIdx] = useState(-1);

  const ref = useRef<HTMLDivElement>(null);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform)?.replace(/scaleY\([^)]*\)\s*/, ''),
    transition,
  };

  const showInputError = (tmpIsValidate?: boolean, tmpIsInputValid?: boolean) => {
    if (tmpIsValidate !== undefined && tmpIsValidate) {
      if (tmpIsInputValid !== undefined) {
        return !tmpIsInputValid;
      }
    }
    return false;
  };

  const isImgPreviewForAnswer = (answerLabel: string) => {
    const currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      return ReactHtmlParser(newLabel[0])[0];
    }

    return null;
  };

  const setImgUrlToLabel = (previewData: JSX.Element) => {
    const imgUrl = renderToString(previewData);

    const newAnswersListComponents = answersListComponents.map((answerItem, itemIdx) => {
      // if the item.label already contains img tag, replace it with new img tag
      if (itemIdx === imageUploadAnswerIdx) {
        if (answerItem.label.includes('<img')) {
          return {
            ...answerItem,
            label: answerItem.label.replace(/<img.*\/>/g, imgUrl),
          };
        }
        return {
          ...answerItem,
          label: answerItem.label + imgUrl,
        };
      }

      return answerItem;
    });

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
    setImageUploadAnswerIdx(-1);
  };

  const handleImageUploadSetting = (value?: JSX.Element) => {
    setShowImageUploadModal(false);

    if (value) {
      setImgUrlToLabel(value);
    }
  };

  const handleOnChangelabel = (
    event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>,
    compoIdx: number,
    answerImgPreview?: JSX.Element,
    onBlur = false
  ) => {
    const newAnswersListComponent = answersListComponents.map((answerItem, itemIdx) => {
      if (itemIdx === compoIdx) {
        let answerImgData = '';
        if (answerImgPreview) {
          answerImgData = renderToString(answerImgPreview);
        }

        return {
          ...answerItem,
          label: event.target.value + answerImgData,
          isValid: true,
        };
      }

      return answerItem;
    });
    setAnswersListComponents(newAnswersListComponent);
    handleAnswerChange(newAnswersListComponent, { ...multipleAnswerConfig }, onBlur);
  };

  const getAnswerLabel = (answerLabel: string) => {
    const currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      return currentLabel.replace(newLabel[0], '');
    }

    return currentLabel;
  };

  const deleteAnswer = (idx: number) => {
    const newAnswersListComponents = [...answersListComponents];

    if (newAnswersListComponents.length === ORIGINAL_FORM_VALIDATION.ANSWER_MIN_SIZE) {
      handleFormValidationError(t('registration.originalForm.validationErrorModal.minAnswer'));
      return;
    }

    newAnswersListComponents.splice(idx, 1);
    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
  };

  const updateAnswerLabelWithoutImg = (answerIndex: number, answerLabel: string) => {
    let currentLabel = answerLabel;

    const imgTagRegex = /<img.*\/>/g;
    const newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      currentLabel = currentLabel.replace(newLabel[0], '');
    }

    const newAnswersListComponents = answersListComponents.map((answerItem, itemIdx) => {
      if (itemIdx === answerIndex) {
        return {
          ...answerItem,
          label: currentLabel,
        };
      }

      return answerItem;
    });

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents);
  };

  const answerImgPreview = isImgPreviewForAnswer(item.label);

  useEffect(() => {
    if (
      !answersListComponents[answerIdx].isOtherAnswer &&
      !answersListComponents[answerIdx].isExclusiveAnswer
    ) {
      setNodeRef(ref.current);
    }
  }, [answersListComponents]);

  return (
    <>
      <ContentBox
        key={`${questionIdx}-${totalQuestions}-${answerIdx}-contentbox`}
        data-testid={`${answerIdx}-sa-answer-component-draggable`}
        style={style}
        ref={ref}
      >
        <AnswerComponent
          key={`${questionIdx}-${totalQuestions}-${answerIdx}-component`}
          data-testid={`${answerIdx}-ma-answer-component`}
        >
          {item.isExclusiveAnswer || item.isOtherAnswer ? (
            <div style={{ marginRight: '17px' }} />
          ) : (
            <div {...attributes} {...listeners}>
              <DragIconWrapper
                data-testid={`${questionIdx}-${answerIdx}-DragIndicatorOutlinedIcon`}
              />
            </div>
          )}

          <Typography
            variant="body3"
            sx={{
              color: PALETTE.text.main,
              ...(isValidate && !item.isValid && { color: PALETTE.error.main }),
            }}
          >
            {`${answerIdx + 1}.`}
          </Typography>

          {item.isExclusiveAnswer || item.isOtherAnswer ? (
            <StyledDisabledText
              data-testid={
                item.isExclusiveAnswer ? 'exclusive-ma-answer-label' : 'other-ma-answer-label'
              }
            >
              {item.label}
            </StyledDisabledText>
          ) : (
            <AnswerInput>
              <InputValidationErrorTooltip
                error={showInputError(isValidate, item.isValid)}
                message={t(
                  getOriginalFormsValidationMsg(
                    getAnswerLabel(item.label),
                    ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE
                  ),
                  {
                    maxLength: ORIGINAL_FORM_VALIDATION.ANSWER_LABEL_MAX_SIZE.toLocaleString(
                      LOCALE_STRING.ja
                    ),
                  }
                )}
              >
                <OutlinedInput
                  sx={{
                    margin: '-15px 5px -15px 10px',
                    color: PALETTE.text.main,
                    fontSize: 14,
                    padding: '10px',
                    background: PALETTE.white,
                    width: '100%',
                    height: '40px',
                  }}
                  error={showInputError(isValidate, item.isValid)}
                  inputProps={{
                    'data-testid': `${answerIdx}-ma-answer-label`,
                  }}
                  placeholder={t('registration.originalForm.manual.saComponent.placeholder')}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={`ma-upload-image-answer-${answerIdx}`}
                        onClick={() => {
                          setShowImageUploadModal(true);
                          setImageUploadAnswerIdx(answerIdx);
                        }}
                      >
                        <CropOriginalOutlinedIcon
                          sx={{
                            color: '#425F77',
                            fontSize: '20px',
                          }}
                        />
                      </IconButton>
                    </InputAdornment>
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    handleOnChangelabel(event, answerIdx, answerImgPreview);
                  }}
                  onBlur={(event: FocusEvent<HTMLInputElement>) => {
                    handleOnChangelabel(event, answerIdx, answerImgPreview, true);
                  }}
                  key={`${answerIdx}-label`}
                  value={getAnswerLabel(item.label)}
                />
              </InputValidationErrorTooltip>
            </AnswerInput>
          )}

          <IconButton
            sx={{ marginLeft: '15px' }}
            aria-label={`${answerIdx}-ma-delete-answer`}
            onClick={(event) => {
              event.stopPropagation();
              deleteAnswer(answerIdx);
            }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </AnswerComponent>

        {answerImgPreview && (
          <ImgPreviewContainer data-testid="answer-image-preview">
            <StyledImg src={answerImgPreview.props.src} alt={answerImgPreview.props.alt} />
            <IconButton
              onClick={() => updateAnswerLabelWithoutImg(answerIdx, item.label)}
              data-testid="deleteAnswerImg"
            >
              <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
            </IconButton>
          </ImgPreviewContainer>
        )}
      </ContentBox>

      {showImageUploadModal && (
        <ImageUploadModal
          isOpen={showImageUploadModal}
          handleClose={() => {
            setShowImageUploadModal(false);
          }}
          handleSetting={handleImageUploadSetting}
        />
      )}
    </>
  );
};

export default SortableItem;
